import { CheckIcon, EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys } from "@/lib/queries";
import { COLORS, type Color, type PropertyOption } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Tag } from "../ui";
import { useFeedback } from "../ui/feedback";
import { MenuDivider, MenuLabel, Popover } from "../ui/Popover";

export function randomColor(exclude: Color[] = []): Color {
  const pool = COLORS.filter((c) => c !== "gray" && !exclude.includes(c));
  const from = pool.length ? pool : COLORS;
  return from[Math.floor(Math.random() * from.length)];
}

export function ColorList({ value, onChange }: { value: Color; onChange: (c: Color) => void }) {
  return (
    <div>
      <MenuLabel>Colors</MenuLabel>
      {COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm capitalize hover:bg-hover"
        >
          <span className={cn("size-4 rounded-[3px]", `tag-${c}`)} />
          <span className="flex-1 text-start">{c}</span>
          {value === c && <CheckIcon className="size-4" />}
        </button>
      ))}
    </div>
  );
}

/** Menu to rename, recolor or delete an option. Used by value editors and settings. */
export function OptionMenu({
  option,
  onUpdate,
  onDelete,
  deleteWarning,
}: {
  option: { id: string; label: string; color: Color };
  onUpdate: (body: { label?: string; color?: Color }) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  deleteWarning?: string;
}) {
  const { confirm } = useFeedback();
  const [label, setLabel] = useState(option.label);
  useEffect(() => setLabel(option.label), [option.label]);
  const commit = () => {
    const v = label.trim();
    if (v && v !== option.label) onUpdate({ label: v });
  };
  return (
    <Popover
      placement="right-start"
      className="w-52 p-1"
      onOpenChange={(open) => !open && commit()}
      trigger={(t) => (
        <button
          ref={t.ref}
          onClick={t.onClick}
          type="button"
          className="rounded p-0.5 text-faint opacity-0 group-hover:opacity-100 hover:bg-active hover:text-fg"
          aria-label="Edit option"
        >
          <EllipsisHorizontalIcon className="size-4" />
        </button>
      )}
    >
      {(close) => (
        <>
          <div className="p-1">
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commit();
                  close();
                }
              }}
              className="field py-1"
              dir="auto"
            />
          </div>
          <button
            type="button"
            onClick={async () => {
              close();
              const ok = await confirm({
                title: `Delete "${option.label}"?`,
                message: deleteWarning ?? "It will be removed from every item that uses it.",
                confirmLabel: "Delete",
                danger: true,
              });
              if (ok) onDelete();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-danger hover:bg-hover"
          >
            <TrashIcon className="size-4" /> Delete
          </button>
          <MenuDivider />
          <ColorList value={option.color} onChange={(color) => onUpdate({ color })} />
        </>
      )}
    </Popover>
  );
}

export function usePropertyOptionActions(propertyId: string) {
  const { pid } = useProject();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.project(pid) });
  const run = async <T,>(p: Promise<T>) => {
    try {
      return await p;
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
      throw err;
    } finally {
      refresh();
    }
  };
  return {
    create: (label: string, color: Color) => run(api.createOption(pid, propertyId, { label, color })),
    update: (id: string, body: { label?: string; color?: Color }) => run(api.updateOption(pid, id, body)),
    remove: (id: string) => run(api.deleteOption(pid, id)),
  };
}

/**
 * Notion-like option picker: search, toggle, and create options inline.
 * `value` holds the selected option ids.
 */
export function SelectEditor({
  propertyId,
  options,
  value,
  multi,
  onChange,
  onClose,
  canManage,
}: {
  propertyId: string;
  options: PropertyOption[];
  value: string[];
  multi: boolean;
  onChange: (ids: string[]) => void;
  onClose: () => void;
  canManage: boolean;
}) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const actions = usePropertyOptionActions(propertyId);
  const byId = new Map(options.map((o) => [o.id, o]));
  const term = q.trim().toLowerCase();
  const filtered = options.filter((o) => o.label.toLowerCase().includes(term));
  const exact = options.some((o) => o.label.toLowerCase() === term);
  const canCreate = canManage && term.length > 0 && !exact;
  const rowCount = filtered.length + (canCreate ? 1 : 0);

  useEffect(() => setActive(0), [q]);

  const toggle = (id: string) => {
    if (multi) {
      onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
      setQ("");
      inputRef.current?.focus();
    } else {
      onChange(value[0] === id ? [] : [id]);
      onClose();
    }
  };

  const create = async () => {
    const label = q.trim();
    if (!label) return;
    const opt = await actions.create(label, randomColor(options.map((o) => o.color)));
    setQ("");
    if (multi) onChange([...value, opt.id]);
    else {
      onChange([opt.id]);
      onClose();
    }
  };

  return (
    <div className="w-72">
      <div className="flex flex-wrap items-center gap-1 border-b border-line bg-hover/50 p-2">
        {value.map((id) => {
          const o = byId.get(id);
          return o ? (
            <Tag key={id} color={o.color} onRemove={() => onChange(value.filter((v) => v !== id))}>
              {o.label}
            </Tag>
          ) : null;
        })}
        <input
          ref={inputRef}
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, rowCount - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (active < filtered.length) toggle(filtered[active].id);
              else if (canCreate) create();
            } else if (e.key === "Backspace" && !q && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          placeholder={canManage ? "Search or create an option…" : "Search options…"}
          className="min-w-24 flex-1 bg-transparent py-0.5 text-sm outline-none placeholder:text-faint"
          dir="auto"
        />
      </div>
      <div className="p-1">
        <MenuLabel>{canManage ? "Select an option or create one" : "Select an option"}</MenuLabel>
        {filtered.map((o, i) => (
          <div
            key={o.id}
            className={cn("group flex items-center gap-1 rounded-md px-2 py-1 hover:bg-hover", i === active && "bg-hover")}
            onMouseEnter={() => setActive(i)}
          >
            <button type="button" onClick={() => toggle(o.id)} className="flex min-w-0 flex-1 items-center gap-2 text-start">
              <Tag color={o.color}>{o.label}</Tag>
              {value.includes(o.id) && <CheckIcon className="ms-auto size-4 shrink-0 text-muted" />}
            </button>
            {canManage && (
              <OptionMenu option={o} onUpdate={(b) => actions.update(o.id, b)} onDelete={() => actions.remove(o.id)} />
            )}
          </div>
        ))}
        {canCreate && (
          <button
            type="button"
            onClick={create}
            onMouseEnter={() => setActive(filtered.length)}
            className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-hover", active === filtered.length && "bg-hover")}
          >
            <span className="text-muted">Create</span>
            <Tag color="gray">{q.trim()}</Tag>
          </button>
        )}
        {!canCreate && filtered.length === 0 && <div className="px-2 py-2 text-sm text-faint">No options</div>}
      </div>
    </div>
  );
}
